
import React, { useState, useRef, useEffect } from 'react';
import { useAppContext } from '../App';
import { checkContentSafety, suggestBiblePassage, checkMediaSafety, getDailyVerse } from '../services/geminiService';
import { Image, Video, Book, Send, AlertCircle, Share2, MessageCircle, Heart, Trash2, Smile, CheckCircle, Copy, X, Quote, MapPin, ExternalLink, UserPlus } from 'lucide-react';
import { Post, BibleVersion, UserRole } from '../types';
import { Link } from 'react-router-dom';

const CHRISTIAN_EMOJIS = ['🙏', '✝️', '🕊️', '⛪', '📖', '🙌', '😇', '🕯️', '🍞', '🍷', '❤️', '🕎', '🛐', '🌿'];

const Feed: React.FC = () => {
  const { posts, addPost, currentUser, churches, t, lang } = useAppContext();
  const [newPostContent, setNewPostContent] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showBibleInput, setShowBibleInput] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [bibleQuery, setBibleQuery] = useState('');
  const [bibleCitation, setBibleCitation] = useState<{reference: string, text: string} | null>(null);
  
  // Media State
  const [selectedFile, setSelectedFile] = useState<{ url: string; type: 'image' | 'video'; base64: string; mimeType: string } | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const handleBibleSearch = async () => {
      if(!bibleQuery) return;
      const result = await suggestBiblePassage(bibleQuery, BibleVersion.NIV, lang);
      setBibleCitation(result);
      setBibleQuery('');
      setShowBibleInput(false);
  };

  const addEmoji = (emoji: string) => {
      setNewPostContent(prev => prev + emoji);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video') => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (type === 'video' && file.size > 10 * 1024 * 1024) {
          setError("Video file is too large for this demo (Max 10MB).");
          return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
          if (event.target?.result) {
              const base64String = event.target.result.toString();
              const mimeType = base64String.split(';')[0].split(':')[1];
              const data = base64String.split(',')[1];

              setSelectedFile({
                  url: base64String,
                  type,
                  base64: data,
                  mimeType
              });
              setError(null);
          }
      };
      reader.readAsDataURL(file);
      e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!newPostContent.trim() && !bibleCitation && !selectedFile) return;
    
    setIsPosting(true);
    setError(null);

    // Safety Checks
    if (newPostContent.trim()) {
        const safetyCheck = await checkContentSafety(newPostContent);
        if (!safetyCheck.safe) {
            setError(`Text Flagged: ${safetyCheck.reason || "Contains inappropriate language."}`);
            setIsPosting(false);
            return;
        }
    }

    if (selectedFile) {
        const mediaCheck = await checkMediaSafety(selectedFile.base64, selectedFile.mimeType);
        if (!mediaCheck.safe) {
            setError(`Media Flagged: ${mediaCheck.reason || "Contains inappropriate visual content."}`);
            setIsPosting(false);
            return;
        }
    }

    const newPost: Post = {
      id: Date.now().toString(),
      authorId: currentUser?.id || 'unknown',
      content: newPostContent,
      timestamp: Date.now(),
      likes: [],
      comments: [],
      bibleCitation: bibleCitation || undefined,
      imageUrl: selectedFile?.type === 'image' ? selectedFile.url : undefined,
      videoUrl: selectedFile?.type === 'video' ? selectedFile.url : undefined
    };

    addPost(newPost);
    setNewPostContent('');
    setBibleCitation(null);
    setSelectedFile(null);
    setShowEmojiPicker(false);
    setIsPosting(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Sidebar: User Card */}
        <div className="hidden lg:block lg:col-span-1 space-y-6">
            <UserCard user={currentUser} church={churches.find(c => c.id === currentUser?.churchId)} />
        </div>

        {/* Center: Feed */}
        <div className="lg:col-span-2 space-y-6">
            {/* Create Post Widget */}
            <div className="bg-white rounded-lg shadow p-4">
                <div className="flex gap-4 mb-4">
                <Link to="/profile">
                    <img 
                        src={currentUser?.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser?.name || 'User')}&background=random`} 
                        className="w-10 h-10 rounded-full bg-gray-200 object-cover" 
                        alt="Avatar" 
                    />
                </Link>
                <div className="flex-1">
                    <textarea 
                        className="w-full border-none resize-none text-gray-700 text-lg placeholder-gray-400 h-20 bg-transparent outline-none transition-colors"
                        placeholder={t('feed_placeholder')}
                        value={newPostContent}
                        onChange={(e) => setNewPostContent(e.target.value)}
                    />
                    
                    {bibleCitation && (
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mb-2 rounded-r relative">
                            <p className="italic text-gray-800">"{bibleCitation.text}"</p>
                            <p className="text-xs font-bold text-blue-700 mt-1">{bibleCitation.reference}</p>
                            <button onClick={() => setBibleCitation(null)} className="absolute top-1 right-2 text-gray-400 hover:text-red-500">&times;</button>
                        </div>
                    )}

                    {selectedFile && (
                        <div className="relative mb-2 rounded-lg overflow-hidden bg-black max-h-60 w-fit">
                            <button onClick={() => setSelectedFile(null)} className="absolute top-2 right-2 bg-black/60 text-white p-1 rounded-full hover:bg-black/80 z-10">
                                <X className="w-4 h-4" />
                            </button>
                            {selectedFile.type === 'image' ? (
                                <img src={selectedFile.url} alt="Preview" className="max-h-60 object-contain" />
                            ) : (
                                <video src={selectedFile.url} controls className="max-h-60" />
                            )}
                        </div>
                    )}

                    {showBibleInput && (
                        <div className="flex gap-2 mb-2 animate-fadeIn">
                            <input 
                                type="text" 
                                placeholder="Search topic (e.g., 'hope', 'fear')" 
                                className="flex-1 bg-transparent border-b border-gray-300 rounded-none px-2 py-2 text-sm focus:outline-none focus:border-blue-600 transition-colors"
                                value={bibleQuery}
                                onChange={(e) => setBibleQuery(e.target.value)}
                            />
                            <button onClick={handleBibleSearch} className="text-xs bg-blue-100 text-blue-700 px-3 rounded hover:bg-blue-200">Find</button>
                        </div>
                    )}

                    {showEmojiPicker && (
                        <div className="flex flex-wrap gap-2 mb-2 bg-gray-50 p-3 rounded-lg animate-fadeIn">
                            {CHRISTIAN_EMOJIS.map(emoji => (
                                <button key={emoji} onClick={() => addEmoji(emoji)} className="text-xl hover:scale-125 transition transform">{emoji}</button>
                            ))}
                        </div>
                    )}
                </div>
                </div>
                
                {error && (
                    <div className="flex items-center gap-2 text-red-600 text-sm mb-4 bg-red-50 p-2 rounded animate-pulse">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                    </div>
                )}

                <input type="file" ref={imageInputRef} accept="image/*" className="hidden" onChange={(e) => handleFileSelect(e, 'image')} />
                <input type="file" ref={videoInputRef} accept="video/*" className="hidden" onChange={(e) => handleFileSelect(e, 'video')} />

                <div className="flex justify-between items-center border-t border-gray-100 pt-3">
                <div className="flex gap-4">
                    <button onClick={() => imageInputRef.current?.click()} className="flex items-center gap-1 text-gray-500 hover:text-blue-600 transition">
                        <Image className="w-5 h-5" /> <span className="text-xs font-medium hidden sm:inline">{t('feed_photo')}</span>
                    </button>
                    <button onClick={() => videoInputRef.current?.click()} className="flex items-center gap-1 text-gray-500 hover:text-blue-600 transition">
                        <Video className="w-5 h-5" /> <span className="text-xs font-medium hidden sm:inline">{t('feed_video')}</span>
                    </button>
                    <button onClick={() => setShowBibleInput(!showBibleInput)} className={`flex items-center gap-1 transition ${showBibleInput ? 'text-blue-600' : 'text-gray-500 hover:text-blue-600'}`}>
                        <Book className="w-5 h-5" /> <span className="text-xs font-medium hidden sm:inline">{t('feed_cite')}</span>
                    </button>
                    <button onClick={() => setShowEmojiPicker(!showEmojiPicker)} className={`flex items-center gap-1 transition ${showEmojiPicker ? 'text-blue-600' : 'text-gray-500 hover:text-blue-600'}`}>
                        <Smile className="w-5 h-5" /> <span className="text-xs font-medium hidden sm:inline">{t('feed_emojis')}</span>
                    </button>
                </div>
                <button 
                    onClick={handleSubmit}
                    disabled={isPosting}
                    className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                    {isPosting ? t('posting') : <><Send className="w-4 h-4" /> {t('post')}</>}
                </button>
                </div>
            </div>

            {/* Post List */}
            {posts.map(post => (
                <PostCard key={post.id} post={post} />
            ))}
        </div>

        {/* Right Sidebar: Verse of the Day */}
        <div className="hidden lg:block lg:col-span-1 space-y-6">
            <div className="sticky top-24 space-y-6">
                <DailyVerseWidget />
                
                {/* Suggestions Widget */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <UserPlus className="w-4 h-4 text-blue-600"/> {t('suggestions')}
                    </h3>
                    <p className="text-xs text-gray-500 italic">Connect with more people to grow your faith network.</p>
                    <Link to="/search?q=a" className="block mt-3 text-center text-xs font-bold text-blue-600 hover:underline">
                        Find People
                    </Link>
                </div>
            </div>
        </div>
    </div>
  );
};

// --- Internal Components ---

const UserCard: React.FC<{ user: any, church: any }> = ({ user, church }) => {
    if(!user) return null;
    return (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 sticky top-24">
            <div className="h-20 bg-blue-600 relative">
                {user.bannerUrl && <img src={user.bannerUrl} className="w-full h-full object-cover opacity-50" alt="" />}
            </div>
            <div className="px-4 pb-4">
                <div className="relative -mt-10 mb-3 flex justify-center">
                    <img 
                        src={user.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=random`} 
                        className="w-20 h-20 rounded-full border-4 border-white bg-white object-cover" 
                        alt={user.name} 
                    />
                </div>
                <div className="text-center mb-4">
                    <h3 className="font-bold text-gray-900 text-lg">{user.name}</h3>
                    <p className="text-sm text-gray-500">{user.voluntaryCall || 'Member'}</p>
                </div>
                
                <div className="border-t border-gray-100 py-3 space-y-2">
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Connections</span>
                        <span className="font-bold text-blue-600">{user.connections?.length || 0}</span>
                    </div>
                    {church && (
                        <div className="flex justify-between text-sm items-center">
                            <span className="text-gray-500">Church</span>
                            <Link to={`/church/${church.id}`} className="font-bold text-gray-800 truncate max-w-[120px] hover:underline hover:text-blue-600">
                                {church.name}
                            </Link>
                        </div>
                    )}
                </div>
                
                <Link to="/profile" className="block w-full py-2 mt-2 text-center text-sm font-bold text-blue-600 border border-blue-100 rounded-lg hover:bg-blue-50 transition">
                    View Profile
                </Link>
            </div>
        </div>
    );
};

const DailyVerseWidget: React.FC = () => {
    const { t, lang } = useAppContext();
    const [verse, setVerse] = useState<{reference: string, text: string, imageKeyword: string} | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchVerse = async () => {
            setLoading(true);
            const data = await getDailyVerse(lang);
            setVerse(data);
            setLoading(false);
        };
        fetchVerse();
    }, [lang]);

    const handleShare = () => {
        if(verse) {
            navigator.clipboard.writeText(`"${verse.text}" - ${verse.reference}`);
            alert("Verse copied to clipboard!");
        }
    };

    if (loading) return (
        <div className="bg-white rounded-lg shadow-sm p-4 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-20 bg-gray-100 rounded mb-4"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
        </div>
    );

    return (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100">
            <div className="h-32 bg-blue-900 relative">
                <img 
                    src={`https://image.pollinations.ai/prompt/christian%20${verse?.imageKeyword || 'nature'}%20scenery?width=400&height=200&nologo=true`}
                    alt="Verse Background" 
                    className="w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-white font-serif italic text-2xl opacity-90 shadow-sm">{t('daily_bread')}</span>
                </div>
            </div>
            <div className="p-5 text-center">
                <Quote className="w-8 h-8 text-blue-200 mx-auto mb-3" />
                <p className="text-gray-800 font-serif italic text-lg leading-relaxed mb-4">
                    "{verse?.text}"
                </p>
                <p className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-4">
                    — {verse?.reference}
                </p>
                <button 
                    onClick={handleShare}
                    className="w-full py-2 bg-blue-50 text-blue-700 font-bold rounded-lg hover:bg-blue-100 transition flex items-center justify-center gap-2 text-sm"
                >
                    <Share2 className="w-4 h-4" /> {t('share_verse')}
                </button>
            </div>
        </div>
    );
};

const PostCard: React.FC<{ post: Post }> = ({ post }) => {
    const { churches, currentUser, removePost, allUsers, toggleLike, addComment, t } = useAppContext();
    const [showComments, setShowComments] = useState(false);
    const [commentText, setCommentText] = useState("");
    const [showShareToast, setShowShareToast] = useState(false);
    const [commentError, setCommentError] = useState<string | null>(null);
    const [isPostingComment, setIsPostingComment] = useState(false);

    const church = churches.find(c => c.id === post.churchId);
    const authorUser = !church ? allUsers.find(u => u.id === post.authorId) : null;
    
    const canDelete = currentUser?.role === UserRole.ADMIN || currentUser?.id === post.authorId;
    const isLiked = currentUser ? post.likes.includes(currentUser.id) : false;

    const timeAgo = (date: number) => {
        const seconds = Math.floor((Date.now() - date) / 1000);
        let interval = seconds / 31536000;
        if (interval > 1) return Math.floor(interval) + "y";
        interval = seconds / 2592000;
        if (interval > 1) return Math.floor(interval) + "mo";
        interval = seconds / 86400;
        if (interval > 1) return Math.floor(interval) + "d";
        interval = seconds / 3600;
        if (interval > 1) return Math.floor(interval) + "h";
        return Math.floor(seconds / 60) + "m";
    };

    const handleShare = () => {
        const url = `${window.location.origin}/post/${post.id}`;
        navigator.clipboard.writeText(url).then(() => {
            setShowShareToast(true);
            setTimeout(() => setShowShareToast(false), 2000);
        });
    };

    const handleCommentSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if(!commentText.trim()) return;
        setIsPostingComment(true);
        setCommentError(null);

        const check = await checkContentSafety(commentText);
        if(!check.safe) {
            setCommentError("Comment flagged: Inappropriate content.");
            setIsPostingComment(false);
            return;
        }

        addComment(post.id, commentText);
        setCommentText("");
        setIsPostingComment(false);
    };

    const authorAvatar = church 
        ? church.logoUrl 
        : (authorUser?.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(authorUser?.name || 'Unknown')}&background=random`);

    const authorName = church ? church.name : (authorUser?.name || "Unknown User");

    return (
        <div className="bg-white rounded-lg shadow p-4 relative group">
            {canDelete && (
                <button 
                    onClick={() => removePost(post.id)}
                    className="absolute top-4 right-4 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition"
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            )}

            <div className="flex items-center mb-3">
                {church ? (
                    <Link to={`/church/${church.id}`}>
                         <img src={authorAvatar} className="w-10 h-10 rounded bg-gray-100 object-cover" alt="Logo" />
                    </Link>
                ) : (
                    <Link to={`/user/${post.authorId}`}>
                        <img src={authorAvatar} className="w-10 h-10 rounded-full bg-gray-200 object-cover" alt="Avatar" />
                    </Link>
                )}
                <div className="ml-3">
                    {church ? (
                        <Link to={`/church/${church.id}`} className="text-sm font-bold text-gray-900 hover:text-blue-600 hover:underline">
                            {authorName}
                        </Link>
                    ) : (
                         <Link to={`/user/${post.authorId}`} className="text-sm font-bold text-gray-900 hover:text-blue-600 hover:underline">
                            {authorName}
                        </Link>
                    )}
                    <p className="text-xs text-gray-500">{timeAgo(post.timestamp)}</p>
                </div>
            </div>

            <p className="text-gray-800 mb-4 whitespace-pre-wrap">{post.content}</p>

            {post.bibleCitation && (
                 <div className="bg-gray-50 border-l-4 border-gray-300 p-4 mb-4 italic text-gray-700 font-serif">
                     "{post.bibleCitation.text}"
                     <div className="text-xs font-bold text-gray-500 mt-2 not-italic font-sans">— {post.bibleCitation.reference}</div>
                 </div>
            )}

            {post.imageUrl && <img src={post.imageUrl} alt="Post" className="rounded-lg w-full object-cover max-h-96 mb-4" />}
            {post.videoUrl && <video src={post.videoUrl} controls className="rounded-lg w-full max-h-96 mb-4 bg-black" />}

            <div className="flex items-center justify-between border-t border-gray-100 pt-3 mt-2">
                <button 
                    onClick={() => toggleLike(post.id)}
                    className={`flex items-center gap-2 text-sm transition ${isLiked ? 'text-red-600' : 'text-gray-500 hover:text-red-600'}`}
                >
                    <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} /> {post.likes.length} {t('likes')}
                </button>
                <button 
                    onClick={() => setShowComments(!showComments)}
                    className="flex items-center gap-2 text-gray-500 hover:text-blue-600 text-sm"
                >
                    <MessageCircle className="w-4 h-4" /> {post.comments.length} {t('comments')}
                </button>
                <button 
                    onClick={handleShare}
                    className="flex items-center gap-2 text-gray-500 hover:text-blue-600 text-sm relative"
                >
                    <Share2 className="w-4 h-4" /> {t('share')}
                    {showShareToast && (
                        <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs py-1 px-2 rounded whitespace-nowrap animate-fadeIn">
                            Link Copied!
                        </span>
                    )}
                </button>
            </div>

            {showComments && (
                <div className="mt-4 pt-4 border-t border-gray-100 animate-fadeIn">
                    <div className="space-y-4 mb-4">
                        {post.comments.length === 0 && <p className="text-sm text-gray-500 italic">No comments yet.</p>}
                        {post.comments.map(comment => {
                             const commentAuthor = allUsers.find(u => u.id === comment.authorId);
                             const commentAvatar = commentAuthor?.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(commentAuthor?.name || 'User')}&background=random`;
                             return (
                                 <div key={comment.id} className="flex gap-2">
                                     <img src={commentAvatar} className="w-8 h-8 rounded-full bg-gray-200" alt="" />
                                     <div className="bg-gray-50 rounded-lg p-2 flex-1">
                                         <p className="text-xs font-bold text-gray-900">{commentAuthor?.name || "Unknown"}</p>
                                         <p className="text-sm text-gray-800">{comment.content}</p>
                                         <p className="text-[10px] text-gray-400 mt-1">{timeAgo(comment.timestamp)}</p>
                                     </div>
                                 </div>
                             );
                        })}
                    </div>
                    
                    {commentError && <p className="text-xs text-red-500 mb-2">{commentError}</p>}
                    
                    <form onSubmit={handleCommentSubmit} className="flex gap-2 items-center">
                        <img 
                            src={currentUser?.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser?.name || 'Me')}&background=random`} 
                            className="w-8 h-8 rounded-full bg-gray-200" 
                            alt="" 
                        />
                        <div className="flex-1 relative">
                            <input 
                                type="text" 
                                className="w-full bg-gray-50 border border-gray-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-blue-500 transition"
                                placeholder={t('write_comment')}
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                            />
                            <button 
                                type="submit" 
                                disabled={!commentText.trim() || isPostingComment}
                                className="absolute right-1 top-1 p-1.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-0 transition"
                            >
                                <Send className="w-3 h-3" />
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default Feed;
